'use client';

import Link from 'next/link';
import { estimateLabelWidth, type MeasureLabel } from '@/src/shared/lib/chart';
import { cn } from '@/src/shared/lib/shadcn';

// Ruler and tooltip slide between points; with reduced motion they jump.
const SLIDE = 'duration-100 ease-out motion-reduce:transition-none';

/** The dashed vertical line at the inspected point. */
export function ChartRuler({ x, top, bottom }: { x: number; top: number; bottom: number }) {
  return (
    <g
      aria-hidden="true"
      className={cn('transition-transform', SLIDE)}
      style={{ transform: `translateX(${x}px)` }}
    >
      <line
        data-testid="chart-ruler"
        x1={0}
        x2={0}
        y1={top}
        y2={bottom}
        className="stroke-muted-foreground"
        strokeWidth={1}
        strokeDasharray="3 3"
      />
    </g>
  );
}

/** A series' value at the inspected point. */
export function ChartDot({ x, y, color }: { x: number; y: number; color: string }) {
  return (
    <circle
      aria-hidden="true"
      cx={0}
      cy={0}
      r={4}
      fill={color}
      stroke="hsl(var(--card))"
      strokeWidth={2}
      className={cn('transition-transform', SLIDE)}
      style={{ transform: `translate(${x}px, ${y}px)` }}
    />
  );
}

export interface ChartPointRow {
  label: string;
  value: string;
  strong?: boolean;
}

const MIN_WIDTH = 176;
// Horizontal padding of the box and the gap between a row's label and its value, in px.
const PAD_X = 24;
const GAP = 16;
// The link a touch-pinned tooltip adds under its rows.
const ACTION_HEIGHT = 24;

/**
 * The tooltip's footprint, to place it before it renders. The box grows to its widest
 * line and never wraps. Without a font measurer the estimate runs wide, so the placement
 * errs away from the edge.
 */
export function chartPointTooltipSize(
  title: string,
  rows: ChartPointRow[],
  measure: MeasureLabel = estimateLabelWidth,
  withAction = false,
): { width: number; height: number } {
  const widest = Math.max(
    measure(title, 12, 500),
    ...rows.map((r) => measure(r.label, 12, 400) + GAP + measure(r.value, 12, 600)),
  );
  return {
    width: Math.max(MIN_WIDTH, Math.ceil(widest) + PAD_X),
    height: 40 + rows.length * 18 + (withAction ? ACTION_HEIGHT : 0),
  };
}

export interface ChartPointLink {
  href: string;
  label: string;
}

/** Where a touch-pinned point opens; a tap on the chart itself only inspects. */
export function ChartPointAction({ href, label }: ChartPointLink) {
  return (
    <Link
      href={href}
      data-chart-action
      className="mt-1 inline-flex min-h-6 items-center font-semibold text-foreground underline underline-offset-[3px]"
    >
      {label}
    </Link>
  );
}

/**
 * What the inspected point holds. It ignores the pointer, so passing over it keeps
 * it open, and screen readers skip it: `ChartAnnouncer` speaks the same values.
 * With an `action` (a touch-pinned point) it takes taps and is read, for its link.
 */
export function ChartPointTooltip({
  title,
  rows,
  left,
  top,
  action,
}: {
  title: string;
  rows: ChartPointRow[];
  left: number;
  top: number;
  action?: ChartPointLink | null;
}) {
  return (
    <div
      aria-hidden={action ? undefined : 'true'}
      data-testid="chart-tooltip"
      className={cn(
        'absolute w-max rounded-lg bg-(--sc-pop) px-3 py-2 text-xs whitespace-nowrap shadow-(--sc-shadow-pop)',
        action ? 'pointer-events-auto' : 'pointer-events-none',
        'transition-[left,top]',
        SLIDE,
      )}
      style={{ left, top, minWidth: MIN_WIDTH }}
    >
      <p className="mb-1 font-medium">{title}</p>
      {rows.map((row) => (
        <p key={row.label} className="flex justify-between gap-4 text-muted-foreground">
          <span>{row.label}</span>
          <span className={cn('tabular-nums', row.strong && 'font-semibold text-foreground')}>
            {row.value}
          </span>
        </p>
      ))}
      {action && <ChartPointAction {...action} />}
    </div>
  );
}

/** Speaks the inspected point to screen readers; empty while nothing is inspected. */
export function ChartAnnouncer({ text }: { text: string | null }) {
  return (
    <p role="status" aria-live="polite" className="sr-only">
      {text ?? ''}
    </p>
  );
}
